/**
 * liveStatus -- how LIVE operational facts render when the device is not live.
 *
 * Owner report (2026-10-06): an OFFLINE AP (903cb32d3021, last contact 1d ago) showed
 * "Clients 19" and a green "Matches pushed config". Both read as current state; neither was.
 *
 * Rules encoded here (pure, so `npm run test:unit` pins them without a DOM):
 *   - Clients = connected NOW. The server already returns clients_count = 0 for an offline
 *     device (services/client_counts); the UI shows "—" rather than a misleading "0", plus a
 *     quiet secondary line with the last-reported count and its age when the server has one.
 *   - Config status: the healthy/warn/critical ramp is for LIVE state only. A verdict on an
 *     offline device (server flag `stale`) is its last word, rendered neutral with its age:
 *     "Last confirmed 1d ago (device offline)". The verdict itself is never rewritten.
 */
import { fmtAbsolute, relTime } from "./format.ts";

/** The device fields the Clients cell needs (a subset of types/domain Device). */
export interface ClientsFacts {
  online: boolean;
  clients_count?: number | null;
  clients_last_known?: number | null;
  clients_last_known_at?: string | null;
}

export interface ClientsDisplay {
  /** The main figure: the connected-now count, or "—" when the device is not online. */
  primary: string;
  /** Secondary muted line, e.g. "19 when last online · 1d ago". null when nothing to add. */
  secondary: string | null;
}

/** Clients cell for the device list / detail. See the module comment for the rules. */
export function clientsDisplay(d: ClientsFacts, rel: typeof relTime = relTime): ClientsDisplay {
  if (d.online) {
    return { primary: String(d.clients_count ?? 0), secondary: null };
  }
  // Offline: no connected clients by definition. Show the last report only when we have one.
  const n = d.clients_last_known;
  const secondary =
    n != null && d.clients_last_known_at
      ? `${n} when last online · ${rel(d.clients_last_known_at)}`
      : null;
  return { primary: "—", secondary };
}

/** Staleness fields the verification endpoints attach to a verdict (config_verify.staleness). */
export interface ConfigStaleness {
  stale?: boolean;
  stale_reason?: string | null;
  last_confirmed_at?: string | null;
}

/**
 * The neutral label for a verdict that is a historical last word (device not online), or null
 * when the verdict is live and should render on the normal ramp.
 *
 * Only VERIFIED / DIVERGED get a stale label: an UNKNOWN verdict is already neutral, and its
 * reason text says why.
 */
export function staleConfigLabel(
  verdict: string | undefined,
  s: ConfigStaleness | undefined,
  rel: typeof relTime = relTime,
): string | null {
  if (!s?.stale || (verdict !== "verified" && verdict !== "diverged")) return null;
  const why = s.stale_reason === "device_offline" || !s.stale_reason ? "device offline" : s.stale_reason;
  const when = s.last_confirmed_at ? ` ${rel(s.last_confirmed_at)}` : "";
  return verdict === "verified"
    ? `Last confirmed${when} (${why})`
    : `Diverged when last checked${when} (${why})`;
}

/* ---------------------------------------------------------------------------------------------
 * Offline SWITCH pages (OP#1860, owner images 19 + 22)
 *
 * Switch > PoE sat on "Polling switch…" forever for an offline switch, and Switch > Ports said
 * "that does not mean the switch is down" about a switch that WAS down. Rules:
 *   - a switch the device list already reports offline is not polled at all; the server also
 *     short-circuits (/switch/ports -> reason "offline" + offline_since) for the race where it
 *     went offline after the list loaded;
 *   - a poll that does not answer within SWITCH_POLL_TIMEOUT_MS ends in a "stopped responding"
 *     state (no silent retry loop) with a Retry;
 *   - an answered poll with no PoE ports says so instead of rendering nothing.
 * ------------------------------------------------------------------------------------------- */

/** How long the PoE card waits for /switch/ports before saying the switch stopped responding. */
export const SWITCH_POLL_TIMEOUT_MS = 20_000;

/** Thrown by withDeadline when the deadline passes first. Not an ApiError: never retried. */
export class PollTimeoutError extends Error {
  readonly ms: number;
  constructor(ms: number) {
    super(`No answer within ${Math.round(ms / 1000)} s`);
    this.name = "PollTimeoutError";
    this.ms = ms;
  }
}

/**
 * Run `fn` with an AbortSignal that fires when the caller's signal does OR after `ms`. When the
 * deadline is what ended it, reject with PollTimeoutError so the UI can tell "stopped
 * responding" from a cancelled query (react-query aborts on unmount / key change).
 */
export async function withDeadline<T>(
  fn: (signal: AbortSignal) => Promise<T>,
  ms: number,
  outer?: AbortSignal,
): Promise<T> {
  const ctl = new AbortController();
  let timedOut = false;
  const onAbort = () => ctl.abort();
  if (outer?.aborted) ctl.abort();
  else outer?.addEventListener("abort", onAbort, { once: true });
  const timer = setTimeout(() => {
    timedOut = true;
    ctl.abort();
  }, ms);
  try {
    // Race too: a fetcher that ignores its signal must still not hang the card.
    return await Promise.race([
      fn(ctl.signal),
      new Promise<never>((_, reject) =>
        ctl.signal.addEventListener("abort", () => timedOut && reject(new PollTimeoutError(ms)), { once: true }),
      ),
    ]);
  } catch (e) {
    if (timedOut) throw new PollTimeoutError(ms);
    throw e;
  } finally {
    clearTimeout(timer);
    outer?.removeEventListener("abort", onAbort);
  }
}

/** The fields of the device-list row the offline decision needs (subset of types/domain Device). */
export interface SwitchLiveness {
  online: boolean;
  status?: string;
  last_seen: string | null;
}

/** True when the list already knows the switch is not live: do not poll it. */
export function switchKnownOffline(d: SwitchLiveness): boolean {
  if (d.status) return d.status === "offline" || d.status === "dormant";
  return !d.online;
}

/**
 * "Switch is offline since 10/3/2026, 12:00:00 PM (15d ago)." -- the absolute time first (what
 * an operator writes in a ticket), the age second. Never seen -> say that, never "since never".
 */
export function offlineSinceText(
  lastSeen: string | null | undefined,
  rel: typeof relTime = relTime,
  abs: typeof fmtAbsolute = fmtAbsolute,
): string {
  if (!lastSeen) return "Switch is offline and has never contacted Meridian.";
  return `Switch is offline since ${abs(lastSeen)} (${rel(lastSeen)}).`;
}

/** The answer of GET /devices/{serial}/switch/ports, as far as the PoE card reads it. */
export interface SwitchPortsAnswer {
  snmp_ok: boolean;
  ports: unknown[];
  reason?: string | null;
  offline_since?: string | null;
}

export type PoeCardState =
  | { kind: "offline"; since: string | null }
  | { kind: "polling" }
  | { kind: "stopped" }
  | { kind: "error" }
  | { kind: "unreachable" }
  | { kind: "no_ports" }
  | { kind: "ports" };

/** One decision for the PoE card, so every branch is unit-tested and none can render "forever". */
export function poeCardState(
  device: SwitchLiveness,
  q: { data?: SwitchPortsAnswer; error: unknown },
): PoeCardState {
  if (switchKnownOffline(device)) return { kind: "offline", since: device.last_seen };
  if (q.data?.reason === "offline") return { kind: "offline", since: q.data.offline_since ?? device.last_seen };
  if (q.error instanceof PollTimeoutError) return { kind: "stopped" };
  if (q.error) return { kind: "error" };
  if (!q.data) return { kind: "polling" };
  if (!q.data.snmp_ok) return { kind: "unreachable" };
  if (q.data.ports.length === 0) return { kind: "no_ports" };
  return { kind: "ports" };
}

/** One PoE-table row whatever the transport: SNMP rows and owgw-state rows use different keys. */
export interface LivePortRow {
  key: string;
  name: string;
  status: "up" | "down" | "unknown";
  speed_mbps: number | null;
  description: string | null;
}

/**
 * /switch/ports answers in two shapes: SNMP {ifindex, name, status, speed_mbps, alias, descr}
 * and owgw-state {port, native_vlan, carrier}. The PoE table read only the SNMP keys, so an
 * owgw-sourced switch rendered rows with no port name and every link "down". Unknown carrier is
 * "unknown", never "down".
 */
export function normalizeLivePort(
  p: {
    ifindex?: number; name?: string; port?: string; carrier?: boolean | null; status?: string;
    speed_mbps?: number | null; alias?: string | null; descr?: string | null;
  },
  i: number,
): LivePortRow {
  const name = p.name ?? p.port ?? `#${i + 1}`;
  const status: LivePortRow["status"] =
    p.status === "up" || p.status === "down" ? p.status : p.carrier == null ? "unknown" : p.carrier ? "up" : "down";
  return {
    key: p.ifindex != null ? `if${p.ifindex}` : `p:${name}:${i}`,
    name,
    status,
    speed_mbps: p.speed_mbps ?? null,
    description: p.alias || p.descr || null,
  };
}

/**
 * Switch > Ports coverage line (OP#1860). The page used to say "that does not mean the switch is
 * down" about EVERY switch with no port list -- including one that was offline. Offline switches
 * are now named as offline; only an ONLINE switch with no port list keeps the "not down" wording.
 */
export function portsCoverageNote(offline: string[], silentOnline: string[]): string[] {
  const out: string[] = [];
  const list = (names: string[]) => names.join(", ");
  if (offline.length > 0) {
    out.push(
      `${list(offline)} ${offline.length === 1 ? "is" : "are"} offline, so live ports are unavailable until ${
        offline.length === 1 ? "it reconnects" : "they reconnect"
      }.`,
    );
  }
  if (silentOnline.length > 0) {
    out.push(
      `Ports not reported by ${list(silentOnline)}: neither uCentral state nor SNMP is giving Meridian a port list for ${
        silentOnline.length === 1 ? "it" : "them"
      }. The switch is online, so this is a reporting gap, not an outage.`,
    );
  }
  return out;
}
